import { useState, useCallback } from 'react'
import { productReady } from '@petrotrack/shared'
import { usePlans } from '../../hooks/useBilling'
import { useFilters } from '../../hooks/useFilters'
import { useDailyData } from '../../hooks/useTankData'
import { todayStr } from '../../utils/dateFilters'
import { useOrgData } from '../../hooks/useOrgData'
import { useUsers } from '../../hooks/useUsers'
import { useCreditModule } from '../../hooks/useCreditModule'
import { useSupport } from '../../hooks/useSupport'
import NavBar from './NavBar'
import BottomNav from './BottomNav'
import SideNav from './SideNav'
import StationCalibrationPage from '../../pages/StationCalibrationPage'
import BillingPage from '../../pages/BillingPage'
import DataEntryModal from '../dataEntry/DataEntryModal'
import ProductSelectSheet from '../common/ProductSelectSheet'
import Toast from '../common/Toast'
import SupportWidget from '../support/SupportWidget'
import ConfirmDialog from '../common/ConfirmDialog'
import HomePage from '../../pages/HomePage'
import AnalyticsPage from '../../pages/AnalyticsPage'
import HistoryPage from '../../pages/HistoryPage'
import ProfilePage from '../../pages/ProfilePage'
import CreditPage from '../../pages/CreditPage'
import TrialBanner from '../dashboard/TrialBanner'

// products present at a branch (from tanks + pumps)
function branchProducts(branch) {
  const s = new Set()
  ;(branch?.tanks || []).forEach((t) => s.add(t.product))
  ;(branch?.pumps || []).forEach((p) => s.add(p.product))
  return [...s].sort()
}

export default function AppShell({ currentUser, onLogout, onDeleteAccount, navigate }) {
  const { filters, setDateFilter, setFuelTypeFilter, setSelectedOrg, setSelectedBranch, setCustomRange, resetFilters } = useFilters()
  const { entries, expenses, addEntry, importEntry, updateEntry, deleteEntry, getLastComplete, refetch: refetchEntries } = useDailyData()
  const { organizations, branches, addOrganization, updateOrganization, deleteOrganization, applyPlanAndTrial, addBranch, updateBranch, deleteBranch, setValve, setPumpCalibration, addProductLine, retireProductLine,
    addTank, updateTank, retireTank, addPump, updatePump, retirePump } = useOrgData()
  const { users, addUser, assignUser, deactivateUser, reactivateUser, resetPassword, deleteUser } = useUsers(currentUser)
  usePlans() // load the DB-backed plan catalogue (prices/limits) once per session
  const creditModule = useCreditModule(currentUser)
  // One source of tickets for both the floating widget and Profile → Support,
  // so the unread badge and the page can never disagree.
  const support = useSupport(currentUser)

  const [activeTab, setActiveTab] = useState('home')
  // Which station the calibration console is showing. Shared with SideNav so the
  // rail tree and the canvas stay on the same station.
  const [calNav, setCalNav] = useState({ view: 'org', stationId: null })
  const [modal, setModal] = useState({ isOpen: false, branch: null, product: null, editingEntry: null, mode: null })
  const [productSheet, setProductSheet] = useState({ isOpen: false, branch: null, products: [] })
  const [toast, setToast] = useState(null)
  const [pendingDelete, setPendingDelete] = useState(null) // entry

  const showToast = useCallback((message, type = 'success') => setToast({ message, type }), [])

  const isAdminish = currentUser.role === 'admin' || currentUser.role === 'superadmin'

  function openEntry(branch, product) {
    // Gate: a product needs BOTH a tank and a pump before a day can be
    // recorded. Admins are sent straight to the branch editor to finish
    // setup; managers are told exactly what to ask their admin for.
    const readiness = productReady(branch, product)
    if (!readiness.ready) {
      setProductSheet({ isOpen: false, branch: null, products: [] })
      if (isAdminish) {
        setCalNav({ view: 'station', stationId: branch.id })
        setActiveTab('calibration')
        showToast(`"${branch.name}" is missing ${readiness.missing.join(' and ')} for ${product} — add ${readiness.missing.length > 1 ? 'them' : 'it'} here.`, 'error')
      } else {
        showToast(`${product} isn't set up yet at "${branch.name}" (missing ${readiness.missing.join(' and ')}). Ask your admin to add ${readiness.missing.length > 1 ? 'them' : 'it'}.`, 'error')
      }
      return
    }
    doOpenEntry(branch, product)
  }

  function doOpenEntry(branch, product) {
    const today = todayStr()
    const productEntries = entries.filter((e) => e.branchId === branch.id && e.product === product)
    const todayEntry = productEntries.find((e) => e.date === today)
    let mode, editingEntry
    if (!todayEntry) { mode = 'morning'; editingEntry = null }
    else if (todayEntry.session === 'morning_open') { mode = 'evening'; editingEntry = todayEntry }
    else { mode = 'edit'; editingEntry = todayEntry }
    setModal({ isOpen: true, branch, product, editingEntry, mode })
    setProductSheet({ isOpen: false, branch: null, products: [] })
  }

  function openEditEntry(branch, entry) {
    const mode = entry.session === 'morning_open' ? 'evening' : 'edit'
    setModal({ isOpen: true, branch, product: entry.product, editingEntry: entry, mode })
  }

  const closeModal = () => setModal({ isOpen: false, branch: null, product: null, editingEntry: null, mode: null })

  async function handleSave(input) {
    const result = await addEntry(input)
    showToast(result.message, result.success ? 'success' : 'error')
    if (result.success) closeModal()
  }
  async function handleUpdate(input, entryId) {
    const result = await updateEntry(input, entryId)
    showToast(result.message, result.success ? 'success' : 'error')
    if (result.success) closeModal()
  }
  const handleDelete = (entry) => setPendingDelete(entry)
  async function confirmDelete() {
    const entry = pendingDelete
    setPendingDelete(null)
    const result = await deleteEntry(entry.id)
    showToast(result.message, result.success ? 'success' : 'error')
  }

  function handleAddPress() {
    if (currentUser.role !== 'manager') return
    const myBranch = branches.find((b) => b.id === currentUser.branchId)
    if (!myBranch) {
      showToast('Your account is not assigned to a branch yet. Ask your admin to assign you.', 'error')
      return
    }
    const products = branchProducts(myBranch)
    if (products.length === 0) {
      showToast(`"${myBranch.name}" has no tanks or pumps yet. Ask your admin to set them up before recording entries.`, 'error')
      return
    }
    if (products.length === 1) openEntry(myBranch, products[0])
    else setProductSheet({ isOpen: true, branch: myBranch, products })
  }

  const canAdd = currentUser.role === 'manager'
  const dayDate = modal.editingEntry?.date ?? todayStr()
  const modalExpenses = modal.branch ? expenses.filter((x) => x.branchId === modal.branch.id && x.date === dayDate) : []

  // Admins get the desktop sidebar at ≥840px; everyone else — and every
  // viewport below that — keeps the bottom navbar.
  const hasSideNav = isAdminish
  // The rail tree lists the same stations the console does.
  const calibrationStations = currentUser.role === 'superadmin'
    ? branches
    : branches.filter((b) => b.organizationId === currentUser.organizationId)

  return (
    <div className={`min-h-screen bg-gray-50 ${hasSideNav ? 'desk:pl-[272px]' : ''}`}>
      {hasSideNav && (
        <div className="hidden desk:block">
          <SideNav activeTab={activeTab} onTabChange={setActiveTab} currentUser={currentUser} onLogout={onLogout}
            organizations={organizations} stations={calibrationStations} calNav={calNav} onCalNavChange={setCalNav} />
        </div>
      )}

      <div className={hasSideNav ? 'desk:hidden' : ''}>
        <NavBar currentUser={currentUser} onLogout={onLogout} onProfileClick={() => setActiveTab('profile')} />
      </div>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-6 pb-28 desk:pb-8">
        <TrialBanner trialEndsAt={currentUser.trialEndsAt} navigate={navigate || (() => {})} />
        {activeTab === 'home' && (
          <HomePage currentUser={currentUser} organizations={organizations} branches={branches}
            entries={entries} expenses={expenses}
            onEnterData={openEntry} onEditEntry={openEditEntry} onDeleteEntry={handleDelete}
            getCreditTodaySummary={creditModule.getTodaySalesSummary}
            getCreditTodaySummaryForOrg={creditModule.getTodaySalesSummaryForOrg} />
        )}
        {activeTab === 'analytics' && (
          <AnalyticsPage currentUser={currentUser} filters={filters}
            onDateFilter={setDateFilter} onFuelTypeFilter={setFuelTypeFilter} onOrgFilter={setSelectedOrg}
            onBranchFilter={setSelectedBranch} onCustomRange={setCustomRange} onReset={resetFilters}
            organizations={organizations} branches={branches} entries={entries} expenses={expenses}
            onEnterData={openEntry} onEditEntry={openEditEntry} onDeleteEntry={handleDelete} />
        )}
        {activeTab === 'history' && (
          <HistoryPage currentUser={currentUser} organizations={organizations} branches={branches}
            entries={entries} onEditEntry={openEditEntry} onDeleteEntry={handleDelete} />
        )}
        {activeTab === 'credit' && (<CreditPage currentUser={currentUser} creditModule={{ ...creditModule, branches }} />)}
        {activeTab === 'billing' && <BillingPage currentUser={currentUser} />}
        {activeTab === 'calibration' && (
          <StationCalibrationPage currentUser={currentUser} organizations={organizations} branches={branches}
            entries={entries} nav={calNav} onNavChange={setCalNav} showToast={showToast}
            onSetValve={setValve} onSetCalibration={setPumpCalibration}
            onAddProductLine={addProductLine} onRetireProductLine={retireProductLine}
            onAddTank={addTank} onUpdateTank={updateTank} onRetireTank={retireTank}
            onAddPump={addPump} onUpdatePump={updatePump} onRetirePump={retirePump}
            onAddStation={addBranch} onUpdateStation={updateBranch} onDeleteStation={deleteBranch} />
        )}
        {(activeTab === 'profile' || activeTab === 'team' || activeTab === 'settings') && (
          <ProfilePage key={activeTab} initialAdminTab={activeTab === 'team' ? 'users' : null}
            currentUser={currentUser} onLogout={onLogout} onDeleteAccount={onDeleteAccount}
            organizations={organizations} branches={branches}
            onAdd={addOrganization} onUpdate={updateOrganization} onDelete={deleteOrganization} onManagePlan={applyPlanAndTrial}
            onAddBranch={addBranch} onUpdateBranch={updateBranch} onDeleteBranch={deleteBranch}
            users={users} onAddUser={addUser} onAssignUser={assignUser} onDeactivateUser={deactivateUser}
            onReactivateUser={reactivateUser} onResetPassword={resetPassword} onDeleteUser={deleteUser}
            onAddEntry={(tankId, entry) => importEntry(entry)} onImported={refetchEntries}
            support={support} />
        )}
      </main>

      <div className={hasSideNav ? 'desk:hidden' : ''}>
        <BottomNav activeTab={activeTab} onTabChange={setActiveTab} canAdd={canAdd} onAddPress={handleAddPress} />
      </div>

      {modal.isOpen && (
        <DataEntryModal isOpen={modal.isOpen} branch={modal.branch} product={modal.product}
          editingEntry={modal.editingEntry} mode={modal.mode} dayExpenses={modalExpenses}
          getLastComplete={getLastComplete} onSave={handleSave} onUpdate={handleUpdate} onClose={closeModal} />
      )}

      {productSheet.isOpen && (
        <ProductSelectSheet products={productSheet.products} branch={productSheet.branch}
          onSelect={(product) => openEntry(productSheet.branch, product)}
          onClose={() => setProductSheet({ isOpen: false, branch: null, products: [] })} />
      )}

      <SupportWidget currentUser={currentUser} support={support} />

      <Toast toast={toast} onDismiss={() => setToast(null)} />

      {pendingDelete && (
        <ConfirmDialog message={`Delete the ${pendingDelete.product} entry for ${pendingDelete.date}? This cannot be undone.`}
          onConfirm={confirmDelete} onCancel={() => setPendingDelete(null)} />
      )}
    </div>
  )
}
